import type { Locale } from 'date-fns';
import { tr, enUS } from 'date-fns/locale';
import { trTR as muiTr, enUS as muiEn } from '@mui/material/locale';
import type { Localization } from '@mui/material/locale';
import { trTR as pickersTr, enUS as pickersEn } from '@mui/x-date-pickers/locales';

// The one list of panel languages. Everything language-specific (locale tags, date libraries,
// switcher entries, text direction) is read from here, so adding a language means adding an
// entry, its dictionary folder (locales/<code>) and the same code in backend/src/i18n/languages.js.

export type LanguageStatus =
  | 'ready'   // offered to everyone
  | 'beta';   // offered to admins only, to review a translation before opening it

export interface LanguageDef {
  code: string;
  /** Name in the language itself, shown in the switcher */
  nativeName: string;
  /** English name, used where a language must be named to a machine (AI output instructions) */
  englishName: string;
  /** BCP 47 tag for Intl / toLocale* formatting */
  locale: string;
  dir: 'ltr' | 'rtl';
  status: LanguageStatus;
  /** Languages tried, in order, for a missing translation before the Turkish source text */
  fallback: readonly string[];
  /** Where a currency symbol goes; when unset, taken from Intl for the locale */
  currencyPosition?: 'before' | 'after';
  /** dayjs locale name (its locale file must be imported where dayjs is configured) */
  dayjs: string;
  dateFns: Locale;
  /** MUI's built-in texts (pagination, "No options") and date picker texts, merged into the theme */
  mui: Localization;
  pickers: object;
}

export const SOURCE_LANGUAGE = 'tr';

export const LANGUAGE_DEFS = [
  {
    code: 'tr', nativeName: 'Türkçe', englishName: 'Turkish', locale: 'tr-TR', dir: 'ltr', status: 'ready',
    // Intl puts ₺ first for tr-TR; the panel has always written "1.234,50 ₺"
    fallback: [], currencyPosition: 'after', dayjs: 'tr', dateFns: tr, mui: muiTr, pickers: pickersTr,
  },
  {
    code: 'en', nativeName: 'English', englishName: 'English', locale: 'en-US', dir: 'ltr', status: 'ready',
    fallback: [], currencyPosition: 'before', dayjs: 'en', dateFns: enUS, mui: muiEn, pickers: pickersEn,
  },
] as const satisfies readonly LanguageDef[];

export type AppLanguage = typeof LANGUAGE_DEFS[number]['code'];

export const LANGUAGE_CODES: AppLanguage[] = LANGUAGE_DEFS.map((d) => d.code);

const BY_CODE = new Map<string, LanguageDef>(LANGUAGE_DEFS.map((d) => [d.code, d]));

/** The panel language for a tag such as 'en', 'en-GB' or a stored value; unknown tags give Turkish */
export function resolveLanguage(tag?: string | null): AppLanguage {
  if (!tag) return SOURCE_LANGUAGE;
  if (BY_CODE.has(tag)) return tag as AppLanguage;
  const base = tag.split(/[-_]/)[0].toLowerCase();
  return (BY_CODE.has(base) ? base : SOURCE_LANGUAGE) as AppLanguage;
}

export function langDef(code: string): LanguageDef {
  return BY_CODE.get(resolveLanguage(code))!;
}

/** Languages this user may pick: ready ones, plus beta ones for admins */
export function availableLanguages(role?: string): LanguageDef[] {
  return LANGUAGE_DEFS.filter((d: LanguageDef) => d.status === 'ready' || (d.status === 'beta' && role === 'admin'));
}
